import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { loadJobTitles } from '../../../../core/store/profile/profile.actions';
import { EmployeeReviewTemplateService } from '../../../../core/services/employee-review-template.service';
import { KraMetricCriterion, KraMetricVersion, MetricDefinition, KraMetricConfiguration, KraMetricResult } from '../../../../models/kraMetricModels';
import { AlertService } from '../../../../shared/services/alert.service';

@Component({ selector: 'app-kra-metrics', templateUrl: './kra-metrics.component.html', standalone: false })
export class KraMetricsComponent extends BaseComponent implements OnInit {
  templateID: number;
  templateName = '';
  loading = true;
  saving = false;
  error = '';
  search = '';
  criterionSearch = '';
  criteria: KraMetricCriterion[] = [];
  library: MetricDefinition[] = [];
  selected: KraMetricCriterion;
  draft: KraMetricVersion;
  original = '';
  jobTitles: { label: string; value: number }[] = [];
  assignedJobTitleIDs: number[] = [];
  evaluationMethods = ['Manager Rating', 'Automated Metric', 'Metric + Manager Rating', 'Manual Metric', 'Informational / No Score'];
  operators = ['Greater than', 'Greater than or equal', 'Less than', 'Less than or equal', 'Equal', 'Between'];
  windows = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'KRA review period', 'Rolling X days'];
  readiness = ['Ready', 'Needs Configuration', 'Additional Tracking Required', 'External Data Required', 'Manual', 'Needs Review'];
  neutralPolicies = ['Reallocate among scored components', 'Preserve weight and mark incomplete', 'Exclude criterion from period score', 'Require manual completion'];
  statuses = ['Met', 'Not Met', 'Neutral / Excluded', 'Insufficient Data', 'Not Configured'];
  conversions = ['Pass/Fail', 'Percentage bands', 'Threshold-to-rating', 'Direct numeric', 'Custom bands'];
  analysisJSON = '';
  employees: {id: number; name: string}[] = [];
  employeeID: number;
  resultVersionID: number;
  periodStart = new Date().toISOString().slice(0, 10);
  periodEnd = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  managerRating: number;
  result: KraMetricResult;
  capture: any = {};
  captureMetricID: number;
  timeZones: {id: string; name: string}[] = [];
  periodTimeZoneID: string;
  captureRevision: number;
  captureRevisionKey = '';
  correctionReason = '';
  requestFingerprint = '';
  requestID = '';


  constructor(private route: ActivatedRoute, private service: EmployeeReviewTemplateService,
    private alerts: AlertService, private store: Store<State>) { super(); }

  ngOnInit() {
    this.templateID = Number(this.route.snapshot.params.templateid);
    this.store.dispatch(loadJobTitles());
    this.subs.push(this.store.select(s => s.profileState.jobTitles).subscribe(rows => {
      this.jobTitles = (rows || []).map(x => ({ label: x.jobTitle, value: x.id }));
    }));
    this.load();
  }

  load() {
    this.loading = true;
    this.error = '';
    this.subs.push(forkJoin({ library: this.service.getMetricLibrary(), criteria: this.service.getMetricCriteria(this.templateID),
      template: this.service.getTemplate(this.templateID), employees: this.service.getMetricEmployees(this.templateID), timeZones: this.service.getMetricTimeZones() }).subscribe({
      next: result => {
        this.employees = result.employees;
        this.timeZones = result.timeZones;
        this.library = result.library;
        this.criteria = result.criteria;
        this.templateName = result.template.description;
        this.assignedJobTitleIDs = result.template.jobTitleIDs || [];
        this.loading = false;
      }, error: err => { this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; this.loading = false; }
    }));
  }

  get filteredCriteria() {
    const query = this.criterionSearch.toLocaleLowerCase();
    return this.criteria.filter(x => `${x.kra} ${x.wording} ${x.context}`.toLocaleLowerCase().includes(query));
  }

  get filteredLibrary() {
    const query = this.search.toLocaleLowerCase();
    return this.library.filter(x => x.active && !this.library.some(y => y.id === x.id && y.version > x.version))
      .filter(x => `${x.name} ${x.category} ${x.requiredInputs}`.toLocaleLowerCase().includes(query));
  }

  get resultVersion() { return this.selected?.history.find(x => x.id === this.resultVersionID); }
  get captureSource() { return this.resultVersion?.metrics.find(x => x.metricDefinitionID === this.captureMetricID); }
  get captureKey() { return JSON.stringify([this.employeeID, this.resultVersionID, this.captureMetricID, this.capture.sourceRecordID]); }

  get applicableJobTitles() { return this.jobTitles.filter(x => this.assignedJobTitleIDs.includes(x.value)); }

  choose(criterion: KraMetricCriterion) {
    if (this.saving) { return; }
    if (this.draft && JSON.stringify(this.draft) !== this.original) {
      this.subs.push(this.alerts.confirm('Unsaved metric settings', 'Discard these settings and select another criterion?')
        .subscribe(confirmed => { if (confirmed) { this.select(criterion); } }));
    } else { this.select(criterion); }
  }

  select(criterion: KraMetricCriterion) {
    this.selected = criterion;
    this.resultVersionID = criterion.configuration.id;
    this.result = undefined;
    this.captureRevisionKey = ''; this.captureRevision = undefined; this.correctionReason = '';
    this.draft = JSON.parse(JSON.stringify(criterion.configuration));
    if (criterion.retired) {
      this.draft.effectiveStart = this.draft.effectiveStart.slice(0, 10);
      this.draft.effectiveEnd = this.draft.effectiveEnd?.slice(0, 10);
      this.original = JSON.stringify(this.draft);
      this.error = '';
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const previous = new Date(`${this.draft.effectiveStart.slice(0, 10)}T00:00:00Z`);
    previous.setUTCDate(previous.getUTCDate() + 1);
    const successor = previous.toISOString().slice(0, 10);
    this.draft.effectiveStart = this.draft.id && successor > today ? successor : today;
    this.draft.effectiveEnd = undefined;
    this.draft.approved = false;
    this.draft.metrics.forEach(metric => metric.activated = false);
    this.original = JSON.stringify(this.draft);
    this.error = '';
  }

  add(definition: MetricDefinition) {
    if (!this.draft || this.saving || this.draft.metrics.some(x => x.metricDefinitionID === definition.id)) { return; }
    this.draft.metrics.push({ metricDefinitionID: definition.id, metricDefinitionVersionID: definition.versionID,
      targetOperator: null, measurementWindow: null, readiness: definition.readiness,
      readinessReason: definition.readinessReason, affectsScore: false, displayOnly: true });
  }

  isConnected(definitionID: number) { return this.draft?.metrics.some(x => x.metricDefinitionID === definitionID); }

  definition(versionID: number) { return this.library.find(x => x.versionID === versionID); }

  migrate() {
    if (this.saving || this.draft && JSON.stringify(this.draft) !== this.original) { this.error = 'Save or discard unsaved metric settings before migration.'; return; }
    this.saving = true;
    this.subs.push(this.service.migrateMetricCandidates(this.templateID).subscribe({ next: report => {
      this.saving = false; this.selected = undefined; this.draft = undefined; this.load(); this.alerts.success(report.message);
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  importAnalysis() {
    if (this.saving || this.draft && JSON.stringify(this.draft) !== this.original) { this.error = 'Save or discard unsaved settings before importing.'; return; }
    let records: KraMetricCriterion[];
    try { records = JSON.parse(this.analysisJSON); } catch { this.error = 'Enter the analysis as a JSON array of criterion configurations.'; return; }
    this.saving = true;
    this.subs.push(this.service.importMetricAnalysis(records).subscribe({ next: () => {
      this.saving = false; this.selected = undefined; this.draft = undefined; this.load(); this.alerts.success('Analysis mappings imported as inactive drafts.');
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : JSON.stringify(err.error) || err.message; } }));
  }

  setConversion(metric: KraMetricConfiguration, value: string) {
    metric.ratingConversion = value === '' ? undefined : { type: Number(value), bands: [] };
  }

  calculate() {
    if (this.saving || !this.resultVersionID || !this.employeeID) { return; }
    const settings = { criterionVersionID: this.resultVersionID, userID: this.employeeID, periodStart: this.periodStart,
      periodEnd: this.periodEnd, periodTimeZoneID: this.periodTimeZoneID, managerRating: this.managerRating };
    const fingerprint = JSON.stringify(settings);
    if (fingerprint !== this.requestFingerprint) { this.requestFingerprint = fingerprint; this.requestID = crypto.randomUUID(); }
    this.saving = true; this.error = '';
    this.subs.push(this.service.calculateMetric({ ...settings, requestID: this.requestID }).subscribe({ next: result => {
      this.result = result; this.saving = false; this.requestFingerprint = '';
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  captureOpportunity() {
    if (this.saving || !this.employeeID || !this.resultVersionID || !this.captureMetricID) { return; }
    const submitted = JSON.parse(JSON.stringify({ criterionVersionID: this.resultVersionID, userID: this.employeeID,
      metricDefinitionID: this.captureMetricID, opportunity: this.capture,
      expectedRevision: this.captureRevisionKey === this.captureKey ? this.captureRevision : 0, correctionReason: this.correctionReason }));
    const key = this.captureKey;
    this.saving = true; this.error = '';
    this.subs.push(this.service.captureMetricOpportunity(submitted).subscribe({ next: record => {
      this.captureRevision = record.revision; this.captureRevisionKey = key; this.correctionReason = '';
      this.saving = false; this.alerts.success('Source opportunity revision saved. Existing result snapshots are preserved; calculate a new result to include the correction.');
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  findOpportunity() {
    if (this.saving || !this.employeeID || !this.resultVersionID || !this.captureMetricID || !this.capture.sourceRecordID) { return; }
    const key = this.captureKey;
    const submitted = { criterionVersionID: this.resultVersionID, userID: this.employeeID,
      metricDefinitionID: this.captureMetricID, opportunity: { sourceRecordID: this.capture.sourceRecordID } };
    this.saving = true; this.error = '';
    this.subs.push(this.service.findMetricOpportunity(submitted).subscribe({ next: record => {
      this.capture = record.opportunity;
      this.capture.opportunityDate = this.capture.opportunityDate.slice(0, 19);
      for (const field of ['startTime', 'dueTime', 'completionTime']) { this.capture[field] = this.capture[field]?.slice(0, 19); }
      this.captureRevision = record.revision; this.captureRevisionKey = key; this.correctionReason = ''; this.saving = false;
      if (!record.timestampsUtc) { this.error = 'This observation has an unknown clock. Confirm its timestamps in the configured source time zone and save a correction with a reason.'; }
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  save() {
    if (this.saving || !this.selected || this.selected.retired || !this.draft) { return; }
    const target = this.selected;
    const submitted: KraMetricCriterion = JSON.parse(JSON.stringify({ ...target, configuration: this.draft, history: [] }));
    this.saving = true;
    this.error = '';
    this.subs.push(this.service.saveMetricCriterion(submitted).subscribe({
      next: result => {
        target.expectedVersionID = result.expectedVersionID;
        const published = target.history.find(x => x.approved);
        if (result.configuration.approved && published && !published.effectiveEnd) { published.effectiveEnd = result.configuration.effectiveStart; }
        target.configuration = result.configuration;
        target.history.unshift(result.configuration);
        this.saving = false;
        this.select(target);
        this.alerts.success('Metric configuration version saved. Existing review scores remain unchanged.');
      }, error: err => {
        this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message;
        this.saving = false;
      }
    }));
  }
}
